import React, { forwardRef, useRef } from 'react';
import styled from 'styled-components';
import { CollapsibleGroupContext } from './CollapsibleGroupContext.js';
import { CollapsibleGroupStyle } from './CollapsibleGroup.styles.js';
import { useCollapsibleGroupState } from './hooks.js';

const defaultProps = {
    accordion: false,
    alignment: 'default',
    alternative: false,
    border: true,
    rounding: 'sharp',
    sizing: 'medium',
    toggle: 'after',
};
const _CollapsibleGroup = forwardRef((props, forwardedRef) => {
    const { accordion, alignment, alternative, border, children, icon, onKeyUp: customOnKeyUpHandler, rounding, sizing, toggle, ...rest } = props;
    const ref = forwardedRef !== null && forwardedRef !== void 0 ? forwardedRef : useRef(null);
    const [state, dispatch] = useCollapsibleGroupState(props);
    /**
     * Ensures that the DOM element belongs to a Collapsible
     * component that is registered to the CollapsibleGroup.
     *
     * @param element The DOM element to check
     * @returns A reference to the element if it belongs to
     * the Collapsible group, `null` otherwise.
     */
    const getCollapsibleRef = (element) => state.refs.find(ref => ref.current === element);
    /**
     * Maps keyboard events to relative sibling elements in
     * the Collapsible group.
     *
     * Implements the optional keyboard interaction in
     * the Accordion pattern.
     *
     * @param key `string` keyboard event named key attribute value
     * @param ref Reference to the current Collapsible
     * @returns
     * @see https://www.w3.org/WAI/ARIA/apg/patterns/accordion/#keyboardinteraction
     */
    const resolveSiblingCollapsible = (key, { current }) => {
        var _a, _b, _c, _d, _e;
        return current &&
            ((_e = {
                ArrowDown: current.nextElementSibling,
                ArrowUp: current.previousElementSibling,
                Home: (_b = (_a = current.parentElement) === null || _a === void 0 ? void 0 : _a.firstElementChild) !== null && _b !== void 0 ? _b : null,
                End: (_d = (_c = current.parentElement) === null || _c === void 0 ? void 0 : _c.lastElementChild) !== null && _d !== void 0 ? _d : null,
            }[key]) !== null && _e !== void 0 ? _e : null);
    };
    const isSummaryElement = (element) => element instanceof HTMLElement && element.nodeName === 'SUMMARY';
    const handleKeyUp = (event) => {
        var _a;
        if (customOnKeyUpHandler) {
            customOnKeyUpHandler(event);
        }
        if (isSummaryElement(event.target)) {
            const { key, target: { parentElement }, } = event;
            const currentCollapsibleRef = getCollapsibleRef(parentElement);
            if (currentCollapsibleRef) {
                const siblingElement = resolveSiblingCollapsible(key, currentCollapsibleRef);
                const siblingRef = getCollapsibleRef(siblingElement);
                const summaryElement = (_a = siblingRef === null || siblingRef === void 0 ? void 0 : siblingRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild;
                if (isSummaryElement(summaryElement)) {
                    event.stopPropagation();
                    summaryElement.focus();
                }
            }
        }
    };
    return (React.createElement("div", { onKeyUp: handleKeyUp, ...rest, ref: ref },
        React.createElement(CollapsibleGroupContext.Provider, { value: {
                accordion,
                alignment,
                alternative,
                border,
                dispatch,
                icon,
                open: state.open,
                rounding,
                sizing,
                toggle,
            } }, children)));
});
_CollapsibleGroup.displayName = '_CollapsibleGroup';
const CollapsibleGroup = styled(_CollapsibleGroup).attrs(({ alternative }) => {
    return {
        alternative: alternative !== null && alternative !== void 0 ? alternative : defaultProps.alternative,
    };
}) `
    ${CollapsibleGroupStyle}
`;

export { CollapsibleGroup, defaultProps };
//# sourceMappingURL=CollapsibleGroup.js.map
